<template>
  <VixConfigProvider :locale="stateLocale === 'zh_CN' ? vixZhCN : vixEnUS">
    <u-config-provider
      spin="round"
      :locale="stateLocale === 'zh_CN' ? zhCN : enUS"
    >
      <router-view />
      <u-modal
        v-model:visible="accountStatusChanged"
        :title="$t('App.425778a7240cea7ef9ce897142085ac6')"
        :closable="false"
        :mask-closable="false"
        :width="380"
        :z-index="3000"
      >
        <div class="account-status-content">
          {{ $t('App.7b4d39fdf7ef836d472e27f715b6fda2') }}
        </div>
        <template #footer>
          <u-button
            type="primary"
            data-testid="account-status-refresh-button"
            @click="refreshAfterAccountStatusChange"
          >
            {{ $t('App.dc268928f2e246855744e6886c92b782') }}
          </u-button>
        </template>
      </u-modal>
      <u-modal
        v-model:visible="spaceStatusChanged"
        :title="$t('App.4bc9fcfb713d6e34736ea891809eaeb9')"
        :closable="false"
        :mask-closable="false"
        :width="380"
        :z-index="3000"
      >
        <div class="space-status-content">
          {{ $t('App.f212118fd754a223289e37d7cad8176c') }}
        </div>
        <template #footer>
          <u-button
            type="primary"
            data-testid="space-status-refresh-button"
            @click="refreshAfterSpaceStatusChange"
          >
            {{ $t('App.dc268928f2e246855744e6886c92b782') }}
          </u-button>
        </template>
      </u-modal>
    </u-config-provider>
  </VixConfigProvider>
</template>
<script>
import { useI18n } from 'vue-i18n';
import {
  defineComponent,
  computed,
  onBeforeUnmount,
  onMounted,
  ref
} from 'vue';
import { debounce } from 'lodash-es';
import { VixConfigProvider } from '@koi-design/vix-components';
import zhCN from '@koi-design/uxd-ui-lang/zh-cn';
import enUS from '@koi-design/uxd-ui-lang/en-us';
import { notice } from '@koi-design/uxd-ui';
import vixZhCN from '@koi-design/vix-components/lang/zh-cn';
import vixEnUS from '@koi-design/vix-components/lang/en-us';
import 'katex/dist/katex.min.css';
import i18n from '@/locales/i18n';
import { useAzureAuth } from '@/hooks/useAzureAuth';
import { usePermission } from '@/hooks/usePermission';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import {
  ACCOUNT_STATUS_SYNC_STORAGE_KEY,
  SPACE_STATUS_SYNC_STORAGE_KEY,
  isAccountStatusDifferent,
  isSpaceStatusDifferent
} from '@/libs/accountSync';
export default defineComponent({
  name: 'VixApp',
  components: {
    VixConfigProvider
  },
  setup() {
    const { t } = useI18n();
    const { initAzureAuth, cleanup } = useAzureAuth();
    const { hasPermission } = usePermission();
    const store = useStore();
    const router = useRouter();
    const accountStatusChanged = ref(false);
    const spaceStatusChanged = ref(false);
    const stateLocale = computed(() => i18n.global.locale.value);
    const isLoginRoute = path => ['/login', '/auth/callback'].includes(path);
    const handleAccountStatusStorage = event => {
      if (
        event.key !== ACCOUNT_STATUS_SYNC_STORAGE_KEY ||
        !event.newValue ||
        isLoginRoute(router.currentRoute.value.path) ||
        !store.getters.botUserInfo?.token ||
        !isAccountStatusDifferent(
          event.newValue,
          store.getters.botUserInfo?.userId
        )
      ) {
        return;
      }
      accountStatusChanged.value = true;
    };
    const handleSpaceStatusStorage = event => {
      if (
        event.key !== SPACE_STATUS_SYNC_STORAGE_KEY ||
        !event.newValue ||
        isLoginRoute(router.currentRoute.value.path) ||
        !store.getters.botUserInfo?.token ||
        !isSpaceStatusDifferent(
          event.newValue,
          store.getters.botUserInfo?.userId,
          store.getters.homelandInfo?.homeland
        )
      ) {
        return;
      }
      spaceStatusChanged.value = true;
    };
    const refreshAfterAccountStatusChange = () => {
      if (!localStorage.getItem('sbtToken')) {
        window.location.assign(`${window.location.origin}/login`);
        return;
      }
      window.location.reload();
    };
    const refreshAfterSpaceStatusChange = () => {
      if (!localStorage.getItem('sbtToken')) {
        window.location.assign(`${window.location.origin}/login`);
        return;
      }
      const targetRoute =
        store.getters.isCompanyUser && !hasPermission('work_space_menu')
          ? '/enterprise'
          : '/workspace';
      window.location.assign(`${window.location.origin}${targetRoute}`);
    };
    const errorHandler = debounce(event => {
      const flag = event.reason.message?.includes(
        'Failed to fetch dynamically imported module'
      );
      if (flag) {
        notice.success({
          title: t('App.582b0d7879ad3f8a902b0053e6a9eec8'),
          desc: t('App.3da3583f8409c11196c857260e6dd400'),
          maxCount: 1,
          duration: 0
        });
      }
    }, 1000);
    onMounted(() => {
      window.addEventListener('unhandledrejection', errorHandler);
      window.addEventListener('storage', handleAccountStatusStorage);
      window.addEventListener('storage', handleSpaceStatusStorage);
      const currentPath = router.currentRoute.value.path;
      const excludePaths = ['/login', '/introduce'];
      if (!excludePaths.includes(currentPath)) {
        const userInfo = store.getters.botUserInfo;
        if (userInfo.token && userInfo.refresh_token) {
          initAzureAuth();
        }
      }
    });
    onBeforeUnmount(() => {
      window.removeEventListener('unhandledrejection', errorHandler);
      window.removeEventListener('storage', handleAccountStatusStorage);
      window.removeEventListener('storage', handleSpaceStatusStorage);
      cleanup();
    });
    return {
      stateLocale,
      accountStatusChanged,
      spaceStatusChanged,
      refreshAfterAccountStatusChange,
      refreshAfterSpaceStatusChange,
      zhCN,
      enUS,
      vixZhCN,
      vixEnUS
    };
  }
});
</script>
<style lang="less">
@import './styles/index.less';

#app {
  :deep(.uui-btn-default-size:not(.uui-btn-variant-link)) {
    --button-height-base: 32px;
    --button-padding-base: 0;
  }
}
</style>
